import { ReactNode } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Sidebar } from './components/Sidebar';
import { Aside } from './components/Aside';
import { Footer } from './components/Footer';
import { ContentHeader } from './components/ContentHeader';
import './styles/_standardModal.scss';

/** Props for the modal shell itself, shared by StandardModal and modals built on it (e.g. WizardModal). */
export interface StandardModalProps {
    id: string;
    isOpen: boolean;
    onClose: () => void;
    /** Called once the modal has finished hiding (after the close animation), e.g. to clear its form data unseen. */
    onHidden?: () => void;
    modalTitle?: string | undefined;
    showModalHeader?: boolean | undefined;
    /** Sidebar width (px number or CSS string). Omit for the default (256px). */
    sidebarWidth?: number | string;
    /** Aside width (px number or CSS string). Omit for the default (160px). */
    asideWidth?: number | string;
    /** Footer height (px number or CSS string). Omit to fit its content. */
    footerHeight?: number | string;
    /** Popup width (px number or CSS string, e.g. 1200 or '80vw'). Omit both width and height for full screen. */
    width?: number | string;
    /**
     * Upper limit on the popup's width (px number or CSS string). Pair it with a relative width (e.g. width '90vw',
     * maxWidth 1000) so the modal shrinks on small screens but doesn't grow past this on large ones.
     */
    maxWidth?: number | string;
    /** Popup height (px number or CSS string, e.g. 800 or '90vh'). Omit both width and height for full screen. */
    height?: number | string;
    hideOnOutsideClick?: boolean;
    /**
     * Always keep room for the content's scrollbar, so the content is the same width whether or not it scrolls (nothing
     * shifts left when the scrollbar appears). Leaves an empty strip at the right while it doesn't scroll.
     */
    reserveScrollbarSpace?: boolean;
    /**
     * Stack the modal just under Material UI's modal layer (z-index 1300) instead of DevExtreme's (1500+), so Material
     * UI dialogs opened from it (e.g. admin's EditEntityModal) appear on top of it rather than behind it. DevExtreme
     * popups (dropdowns, date pickers, load panels) still open above everything.
     */
    stackBelowDialogs?: boolean;
    className?: string;
}

/** What goes inside StandardModal's regions. Modals built on StandardModal (e.g. WizardModal) fill these themselves. */
export interface StandardModalLayoutProps {
    /** Main body of the modal. Always rendered. */
    children: ReactNode;
    /** Heading in the top bar, above the scrolling content. Omit to hide. */
    contentHeader?: string;
    /** Optional guidance shown under contentHeader. */
    contentHelpText?: string;
    /** Sidebar title, shown under the logo. Providing it (or `sidebar`) shows the sidebar. */
    sidebarTitle?: string;
    /** Sidebar content below the title (e.g. navigation, step list). */
    sidebar?: ReactNode;
    /** Actions at the right of the top bar, beside the content header (e.g. "Save and Exit"). Omit to hide. */
    aside?: ReactNode;
    /** Bottom bar content beneath the content (e.g. Back / Save & Continue). Omit to hide. */
    footer?: ReactNode;
}

/**
 * Base modal with optional sidebar, aside and footer regions. Opens full screen unless
 * a width and/or height is provided; a dimension that isn't provided fills the screen.
 * Regions that aren't provided are not rendered, and the layout closes up around them.
 *
 * ┌─────────┬────────────────────────────────┐
 * │  logo   │ contentHeader            aside │  <- top bar (doesn't scroll)
 * │  title  ├────────────────────────────────┤
 * │ sidebar │            children            │  <- scrolls, full width (scrollbar on the right)
 * │         ├────────────────────────────────┤
 * │         │             footer             │
 * └─────────┴────────────────────────────────┘
 */
export const StandardModal = ({
    id,
    isOpen,
    onClose,
    onHidden,
    children,
    contentHeader,
    contentHelpText,
    modalTitle,
    showModalHeader = false,
    sidebarTitle,
    sidebar,
    aside,
    footer,
    sidebarWidth,
    asideWidth,
    footerHeight,
    width,
    maxWidth,
    height,
    hideOnOutsideClick = false,
    reserveScrollbarSpace = false,
    stackBelowDialogs = false,
    className,
}: StandardModalProps & StandardModalLayoutProps) => {
    const showSidebar = sidebarTitle !== undefined || sidebar !== undefined;
    const isFullScreen = width === undefined && height === undefined;

    return (
        <Popup
            id={id}
            visible={isOpen}
            fullScreen={isFullScreen}
            width={isFullScreen ? undefined : (width ?? '100vw')}
            maxWidth={isFullScreen ? undefined : maxWidth}
            height={isFullScreen ? undefined : (height ?? '100vh')}
            showTitle={showModalHeader}
            title={modalTitle}
            dragEnabled={false}
            hideOnOutsideClick={hideOnOutsideClick}
            onHiding={onClose}
            onHidden={onHidden}
            wrapperAttr={{ class: `standardModalPopup${stackBelowDialogs ? ' stackBelowDialogs' : ''}` }}
        >
            <div className={`standardModal${showSidebar ? '' : ' noSidebar'} ${className ?? ''}`}>
                {showSidebar && (
                    <Sidebar
                        title={sidebarTitle ?? ''}
                        width={sidebarWidth}
                    >
                        {sidebar}
                    </Sidebar>
                )}

                <div className='standardModal-main'>
                    {(contentHeader || aside) && (
                        <div className='standardModal-top'>
                            <div className='standardModal-topHeader'>
                                {contentHeader && (
                                    <ContentHeader
                                        header={contentHeader}
                                        helpText={contentHelpText}
                                    />
                                )}
                            </div>
                            {aside && <Aside width={asideWidth}>{aside}</Aside>}
                        </div>
                    )}
                    <div className={`standardModal-body${reserveScrollbarSpace ? ' reserveScrollbarSpace' : ''}`}>
                        <div className='standardModal-content'>{children}</div>
                    </div>
                    {footer && <Footer height={footerHeight}>{footer}</Footer>}
                </div>
            </div>
        </Popup>
    );
};
